import { BoxGeometry, Color, ConeGeometry, CylinderGeometry, Mesh, MeshStandardMaterial, OctahedronGeometry, PointLight } from 'three';
import { clamp, mulberry32 } from '../util/math.js';
import { createModelProp, pickModelPropArchetype } from './prop_models.js';

const geometries = {
  cube: new BoxGeometry(1, 1, 1),
  cylinder: new CylinderGeometry(1, 1, 1, 10),
  cone: new ConeGeometry(1, 1, 7),
  crystal: new OctahedronGeometry(1, 0),
};

// AAA materials — emissive values > 1 feed the bloom pass
const materials = {
  // Arena walls: dark concrete with bright neon trim
  wall: new MeshStandardMaterial({
    color: 0x0d1929,
    roughness: 0.55,
    metalness: 0.28,
    emissive: new Color(0x0a1a2e),
    emissiveIntensity: 0.4,
  }),
  // Glowing neon trim strips — high emissive so bloom picks them up
  wallGlowGreen: new MeshStandardMaterial({
    color: 0x53ffb5,
    emissive: new Color(0x53ffb5),
    emissiveIntensity: 3.5,
    roughness: 0.1,
    metalness: 0.0,
  }),
  wallGlowPurple: new MeshStandardMaterial({
    color: 0x8866ff,
    emissive: new Color(0x8866ff),
    emissiveIntensity: 2.8,
    roughness: 0.1,
    metalness: 0.0,
  }),
  // Props: dark steel-blue base
  prop: new MeshStandardMaterial({
    color: 0x1e3248,
    roughness: 0.52,
    metalness: 0.55,
    emissive: new Color(0x0a1520),
    emissiveIntensity: 0.3,
  }),
  // Props variant: slightly brighter with faint purple tint
  prop2: new MeshStandardMaterial({
    color: 0x2a3f5a,
    roughness: 0.46,
    metalness: 0.62,
    emissive: new Color(0x10182a),
    emissiveIntensity: 0.35,
  }),
  // Prop accent glow — small emissive panels on props
  propGlow: new MeshStandardMaterial({
    color: 0x53ffb5,
    emissive: new Color(0x53ffb5),
    emissiveIntensity: 2.0,
    roughness: 0.05,
    metalness: 0.0,
  }),
  hazard: new MeshStandardMaterial({
    color: 0x47202c,
    roughness: 0.38,
    metalness: 0.72,
    emissive: new Color(0x2b0711),
    emissiveIntensity: 0.48,
  }),
  glass: new MeshStandardMaterial({
    color: 0x233a52,
    roughness: 0.18,
    metalness: 0.18,
    emissive: new Color(0x06192a),
    emissiveIntensity: 0.52,
  }),
};

let obstacleState = null;

function randWith(rng, min, max) {
  return min + rng() * (max - min);
}

export function initObstacles(world, options = {}) {
  createArenaWalls(world);
  createProps(world, options);
  obstacleState = { world, materials, geometries };
  return obstacleState;
}

export function createArenaWalls(world) {
  world.wallRoot.clear();
  const h = world.half;
  const wallSpecs = [
    { x: 0,      z: -h - 0.6, sx: world.size + 2, sz: 1.2 },
    { x: 0,      z:  h + 0.6, sx: world.size + 2, sz: 1.2 },
    { x: -h - 0.6, z: 0,      sx: 1.2, sz: world.size + 2 },
    { x:  h + 0.6, z: 0,      sx: 1.2, sz: world.size + 2 },
  ];

  wallSpecs.forEach((spec, i) => {
    const isHoriz = i < 2;

    // Main wall body
    const wall = new Mesh(geometries.cube, materials.wall);
    wall.position.set(spec.x, 1.4, spec.z);
    wall.scale.set(spec.sx, 2.8, spec.sz);
    wall.castShadow = true;
    wall.receiveShadow = true;
    wall.userData.aabb = {
      minX: spec.x - spec.sx / 2,
      maxX: spec.x + spec.sx / 2,
      minZ: spec.z - spec.sz / 2,
      maxZ: spec.z + spec.sz / 2,
    };
    wall.userData.collidable = true;
    wall.userData.wall = true;
    world.wallRoot.add(wall);

    // Top neon trim — green
    const topStrip = new Mesh(geometries.cube, materials.wallGlowGreen);
    topStrip.position.set(spec.x, 2.82, spec.z);
    topStrip.scale.set(
      isHoriz ? spec.sx : 0.06,
      0.06,
      isHoriz ? 0.06 : spec.sz,
    );
    world.wallRoot.add(topStrip);

    // Mid-height accent trim — purple (every other wall)
    const midStrip = new Mesh(geometries.cube, i % 2 === 0 ? materials.wallGlowPurple : materials.wallGlowGreen);
    midStrip.position.set(spec.x, 1.2, spec.z);
    midStrip.scale.set(
      isHoriz ? spec.sx : 0.05,
      0.05,
      isHoriz ? 0.05 : spec.sz,
    );
    world.wallRoot.add(midStrip);

    // Point light at each corner for colour bounce
    if (i === 0 || i === 2) {
      const corner = new PointLight(i === 0 ? 0x53ffb5 : 0x8866ff, 6.0, 22, 2.0);
      corner.position.set(
        i === 0 ? 0 : spec.x,
        3.5,
        i === 0 ? spec.z : 0,
      );
      world.wallRoot.add(corner);
    }
  });
}

export function createProps(world, options = {}) {
  const {
    seed = 19840508,
    count = 42,
    minAbs = 0,
    pattern = 'open',
    accentColor = 0x53ffb5,
  } = options;
  const rng = mulberry32(seed);
  const accentMat = getAccentMaterial(accentColor);

  for (let i = 0; i < count; i++) {
    const archetype = pickModelPropArchetype(rng, pattern, i);

    let x = 0;
    let z = 0;
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const placed = placePropByPattern(rng, world, minAbs, pattern, i);
      x = placed.x;
      z = placed.z;
      if (Math.hypot(x, z) >= 8 && Math.max(Math.abs(x), Math.abs(z)) >= minAbs) break;
    }
    if (Math.hypot(x, z) < 8) {
      x += Math.sign(x || 1) * 11;
      z += Math.sign(z || 1) * 9;
    }

    const height = randWith(rng, archetype.height[0], archetype.height[1]);
    const rotation = rng() * Math.PI * 2;
    const prop = createModelProp(archetype, { x, z, height, rotation }) || createFallbackProp(rng, pattern, i, x, z, height, rotation);
    prop.userData.accentColor = accentColor;
    world.propRoot.add(prop);

    if (rng() > 0.86 && !prop.userData.propAsset?.includes('light')) {
      const glowPanel = new Mesh(
        geometries.cube,
        rng() > 0.35 ? accentMat : (rng() > 0.5 ? materials.wallGlowGreen : materials.wallGlowPurple),
      );
      glowPanel.position.set(x, height + 0.04, z);
      glowPanel.rotation.y = rotation;
      glowPanel.scale.set(prop.userData.radius * 0.55, 0.045, 0.08);
      glowPanel.userData.collidable = false;
      world.propRoot.add(glowPanel);
    }
  }
}

function createFallbackProp(rng, pattern, index, x, z, height, rotation) {
  const archetype = pickPropArchetype(rng, pattern, index);
  const baseMat = archetype.material || (rng() > 0.5 ? materials.prop : materials.prop2);
  const mesh = new Mesh(geometries[archetype.geometry] || geometries.cube, baseMat);
  mesh.position.set(x, height / 2, z);
  mesh.scale.set(
    randWith(rng, archetype.sx[0], archetype.sx[1]),
    height,
    randWith(rng, archetype.sz[0], archetype.sz[1]),
  );
  mesh.rotation.y = rotation;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.collidable = true;
  mesh.userData.radius = Math.max(mesh.scale.x, mesh.scale.z) * 0.72;
  mesh.userData.aabb = getObjectAabb(mesh);
  return mesh;
}

function pickPropArchetype(rng, pattern, index) {
  const props = [
    { geometry: 'cylinder', height: [1.8, 4.4], sx: [0.45, 1.15], sz: [0.45, 1.15] },
    { geometry: 'cube', height: [0.55, 1.15], sx: [1.6, 3.4], sz: [0.42, 0.9], material: materials.hazard },
    { geometry: 'cube', height: [1.1, 2.6], sx: [0.7, 1.55], sz: [0.7, 1.55] },
    { geometry: 'cone', height: [1.2, 3.2], sx: [0.55, 1.2], sz: [0.55, 1.2], material: materials.glass },
    { geometry: 'crystal', height: [0.9, 2.1], sx: [0.5, 1.0], sz: [0.5, 1.0], material: materials.glass },
  ];
  if (pattern === 'choke' || pattern === 'gauntlet') return props[1 + (index % 2)];
  if (pattern === 'clusters') return props[3 + (index % 2)];
  return props[Math.floor(rng() * props.length)];
}

function placePropByPattern(rng, world, minAbs, pattern, index) {
  const edge = world.size * 0.43;
  const ring = Math.max(10, minAbs + randWith(rng, 3, 10));
  const sign = rng() > 0.5 ? 1 : -1;
  const lane = (Math.floor(index / 3) % 2 === 0 ? -1 : 1) * randWith(rng, 6, edge);

  if (pattern === 'lanes') {
    return index % 2 === 0
      ? { x: lane, z: sign * ring }
      : { x: sign * ring, z: lane };
  }
  if (pattern === 'rings') {
    const a = rng() * Math.PI * 2;
    return { x: Math.cos(a) * ring, z: Math.sin(a) * ring };
  }
  if (pattern === 'choke') {
    return index % 3 === 0
      ? { x: sign * randWith(rng, 3, 8), z: (rng() > 0.5 ? 1 : -1) * ring }
      : { x: sign * ring, z: randWith(rng, -8, 8) };
  }
  if (pattern === 'clusters') {
    const center = index % 4;
    const cx = center < 2 ? -ring : ring;
    const cz = center % 2 === 0 ? -ring : ring;
    return { x: cx + randWith(rng, -5, 5), z: cz + randWith(rng, -5, 5) };
  }
  if (pattern === 'gauntlet') {
    const axis = index % 2 === 0 ? 'x' : 'z';
    return axis === 'x'
      ? { x: sign * ring, z: randWith(rng, -edge * 0.45, edge * 0.45) }
      : { x: randWith(rng, -edge * 0.45, edge * 0.45), z: sign * ring };
  }
  return {
    x: (rng() - 0.5) * world.size * 0.86,
    z: (rng() - 0.5) * world.size * 0.86,
  };
}

function getAccentMaterial(color) {
  const key = `accent${color}`;
  if (!materials[key]) {
    materials[key] = new MeshStandardMaterial({
      color,
      emissive: new Color(color),
      emissiveIntensity: 2.4,
      roughness: 0.08,
      metalness: 0.0,
    });
  }
  return materials[key];
}

export function getObjectAabb(object) {
  const radius = object.userData.radius || Math.max(object.scale.x, object.scale.z) * 0.72;
  return {
    minX: object.position.x - radius,
    maxX: object.position.x + radius,
    minZ: object.position.z - radius,
    maxZ: object.position.z + radius,
  };
}

export function collidesWithObstacle(position, radius, world = obstacleState?.world) {
  if (!world) return false;
  return world.propRoot.children.some(
    (prop) => prop.userData.collidable && prop.userData.aabb && circleIntersectsAabb(position, radius, prop.userData.aabb),
  );
}

export function segmentIntersectsObstacle(start, end, radius, world = obstacleState?.world) {
  if (!world) return null;

  const roots = [world.propRoot, world.wallRoot];
  for (const root of roots) for (const prop of root.children) {
    if (!prop.userData.collidable) continue;
    if (prop.userData.aabb && segmentIntersectsAabb(start, end, prop.userData.aabb, radius)) {
      return prop;
    }
    const obstacleRadius = (prop.userData.radius || 0.8) + radius;
    if (segmentDistanceSq(start, end, prop.position) <= obstacleRadius * obstacleRadius) {
      return prop;
    }
  }

  return null;
}

export function resolveObstacleCollision(position, radius, world = obstacleState?.world) {
  if (!world) return position;

  position.x = clamp(position.x, -world.half + radius, world.half - radius);
  position.z = clamp(position.z, -world.half + radius, world.half - radius);

  for (const prop of world.propRoot.children) {
    if (!prop.userData.collidable) continue;
    const r = (prop.userData.radius || 0.8) + radius;
    const dx = position.x - prop.position.x;
    const dz = position.z - prop.position.z;
    const dist = Math.hypot(dx, dz);
    if (dist < r) {
      const nx = dist > 0.001 ? dx / dist : 1;
      const nz = dist > 0.001 ? dz / dist : 0;
      const push = r - dist;
      position.x += nx * push;
      position.z += nz * push;
    }
  }

  position.x = clamp(position.x, -world.half + radius, world.half - radius);
  position.z = clamp(position.z, -world.half + radius, world.half - radius);

  return position;
}

function circleIntersectsAabb(position, radius, aabb) {
  const cx = clamp(position.x, aabb.minX, aabb.maxX);
  const cz = clamp(position.z, aabb.minZ, aabb.maxZ);
  const dx = position.x - cx;
  const dz = position.z - cz;
  return dx * dx + dz * dz <= radius * radius;
}

function segmentIntersectsAabb(start, end, aabb, radius) {
  const minX = aabb.minX - radius;
  const maxX = aabb.maxX + radius;
  const minZ = aabb.minZ - radius;
  const maxZ = aabb.maxZ + radius;
  const sx = start.x;
  const sz = start.z;
  const dx = end.x - sx;
  const dz = end.z - sz;
  let tMin = 0;
  let tMax = 1;
  if (!clipAxis(sx, dx, minX, maxX, (v) => { tMin = Math.max(tMin, v[0]); tMax = Math.min(tMax, v[1]); })) return false;
  if (!clipAxis(sz, dz, minZ, maxZ, (v) => { tMin = Math.max(tMin, v[0]); tMax = Math.min(tMax, v[1]); })) return false;
  return tMax >= tMin;
}

function clipAxis(origin, delta, min, max, apply) {
  if (Math.abs(delta) < 0.000001) return origin >= min && origin <= max;
  const t1 = (min - origin) / delta;
  const t2 = (max - origin) / delta;
  apply([Math.min(t1, t2), Math.max(t1, t2)]);
  return true;
}

function segmentDistanceSq(start, end, point) {
  const ax = start.x;
  const az = start.z;
  const bx = end.x;
  const bz = end.z;
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq <= 0.000001) {
    const sx = point.x - ax;
    const sz = point.z - az;
    return sx * sx + sz * sz;
  }

  const t = clamp(((point.x - ax) * dx + (point.z - az) * dz) / lenSq, 0, 1);
  const cx = ax + dx * t;
  const cz = az + dz * t;
  const px = point.x - cx;
  const pz = point.z - cz;
  return px * px + pz * pz;
}
